import { createClient, type SupabaseClient } from '@supabase/supabase-js'

const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL as string)?.trim()
const supabaseAnonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY as string)?.trim()

if (!supabaseUrl || !supabaseAnonKey) {
    console.warn(
        '[MoniFlow] Missing Supabase env vars. Add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to .env.local'
    )
}

// ── Singleton + Web Lock bypass ────────────────────────────────────────────
//
// Why: Supabase GoTrue uses the browser Web Locks API (navigator.locks) to
// serialize auth operations across tabs. Vite HMR re-evaluates modules on
// every file save, and React 18 Strict Mode double-mounts components —
// both create new GoTrueClient instances that "steal" the lock from the
// previous one, triggering:
//   AbortError: Lock broken by another request with the 'steal' option
//
// Fix 1 (globalThis singleton): re-use one client instance across HMR reloads.
// Fix 2 (custom lock fn): skip the Web Locks API entirely during development.
//   In production (a single tab, no HMR) the lock is never needed because
//   operations are serialized by the Promise queue internally anyway.

declare global {
    // eslint-disable-next-line no-var
    var __supabaseClient: SupabaseClient | undefined
}

// Custom in-memory lock to serialize auth operations safely without relying
// on navigator.locks (which deadlocks on Vite HMR reloads).
let authLock = Promise.resolve()

if (!globalThis.__supabaseClient) {
    globalThis.__supabaseClient = createClient(supabaseUrl ?? '', supabaseAnonKey ?? '', {
        auth: {
            persistSession: true,
            storageKey: 'moniflow-auth',
            autoRefreshToken: true,
            detectSessionInUrl: true,
            lock: <R>(_name: string, _acquireTimeout: number, fn: () => Promise<R>): Promise<R> => {
                const next = authLock.then(() => fn())
                // Ensure authLock is always a resolved promise regardless of fn failure
                authLock = next.catch((err) => {
                    console.warn('[Supabase] Auth operation failed:', err?.message || err)
                }) as Promise<void>
                return next
            },
        },
    })
}

export const supabase: SupabaseClient = globalThis.__supabaseClient
